/// <amd-module name="VSSUI/Components/Sizer/Sizer" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { Observer } from "VSSUI/Observer";
import { Portal } from "VSSUI/Portal";
import { css, KeyCode, Mouse } from "VSSUI/Util";
import { ISizedProps, ISizerProps, Orientation, Position } from "./Sizer.Props";
import { Tooltip } from "VSSUI/TooltipEx";
import * as Resources from "VSSUI/Resources.Widgets";

export interface ISizerState {
    showPortal: boolean;
}

export class Sizer extends React.Component<ISizerProps, ISizerState> {
    public static defaultProps: Partial<ISizerProps> = {
        divider: true,
        maxSize: 10000,
        minSize: 100
    };

    private debouncedEnd: () => void;
    private lastLocation: number = -1;
    private timerManagement = new TimerManagement();

    constructor(props: Readonly<ISizerProps>) {
        super(props);

        this.debouncedEnd = this.timerManagement.debounce(this.onSizeEnd, 500, { trailing: true });
    }

    state = { showPortal: false };

    public render(): JSX.Element | null {
        const { divider } = this.props;

        return (
            <Tooltip text={Resources.SizerAnnouncement}>
                <div
                    aria-label={this.props.ariaLabel || Resources.SizerAnnouncement}
                    aria-valuetext={`${ObservableLike.getValue(this.props.size)} pixels wide`}
                    aria-valuenow={ObservableLike.getValue(this.props.size)}
                    className={css(
                        this.props.className,
                        "bolt-sizer",
                        this.props.orientation === Orientation.row ? "bolt-sizer-row flex-row" : "bolt-sizer-column flex-column",
                        divider && "divider"
                    )}
                    id={this.props.id}
                    onKeyDown={this.onKeyDown}
                    onMouseDown={this.onMouseDown}
                    onDragStart={this.onDragStart}
                    role="separator"
                    tabIndex={this.props.tabIndex}
                >
                    {this.state.showPortal && (
                        <Portal
                            className={css(
                                "bolt-sizer-portal",
                                this.props.orientation === Orientation.row ? "bolt-sizer-portal-row" : "bolt-sizer-portal-column"
                            )}
                        >
                            <div />
                        </Portal>
                    )}
                </div>
            </Tooltip>
        );
    }

    public componentWillUnmount() {
        Mouse.releaseCapture(this.onMouseCapture);
        this.timerManagement.dispose();
    }

    private onDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        // Prevent default to stop the ColumnDragDropBehavior from trying to handle this event.
        event.preventDefault();
    };

    private onMouseCapture = (event: MouseEvent) => {
        // Recompute the size and update the lastLocation based on the amount changed.
        this.lastLocation += this.updateSize(event, this.getMouseLocation(event) - this.lastLocation);

        // Remove the portal now that we are no longer sizing.
        if (event.type === "mouseup") {
            this.setState({ showPortal: false });

            // If the user wanted to know when the sizing was complete, we will notify
            // the now that sizing is complete.
            this.onSizeEnd();
        }
    };

    private getMouseLocation(event: MouseEvent): number {
        return this.props.orientation === Orientation.row ? event.pageX : event.pageY;
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            const { orientation } = this.props;
            let sizeChange: number | undefined;

            if (orientation === Orientation.row) {
                if (event.which === KeyCode.enter) {
                    let focuselt = this.props.getFocusedElement(event);
                    focuselt?.current?.focus();
                }
                if (event.which === KeyCode.leftArrow) {
                    sizeChange = this.updateSize(event.nativeEvent, -1, true);
                } else if (event.which === KeyCode.rightArrow) {
                    sizeChange = this.updateSize(event.nativeEvent, 1, true);
                }
            } else {
                if (event.which === KeyCode.upArrow) {
                    sizeChange = this.updateSize(event.nativeEvent, -1, true);
                } else if (event.which === KeyCode.downArrow) {
                    sizeChange = this.updateSize(event.nativeEvent, 1, true);
                }
            }

            // If we changed the size, we will prevent the default and start a
            // timer to end the sizing operation. Since there is no gesture to
            // start and complete with the keyboard.
            if (sizeChange) {
                event.preventDefault();
                this.debouncedEnd();
            }
        }
    };

    private onSizeEnd = () => {
        if (this.props.onSizeEnd) {
            this.props.onSizeEnd(this.props.id);
        }
    };

    private onMouseDown = (event: React.MouseEvent<HTMLElement>) => {
        // Look for only main button clicks, not right click
        if (!event.defaultPrevented && event.button === 0) {
            this.lastLocation = this.getMouseLocation(event.nativeEvent);

            // Capture the mouse, this will let us size the column even when the mouse moves
            // outside our element.
            Mouse.setCapture(this.onMouseCapture);

            // Show the portal that keeps our sizing actions for effecting the other elements.
            this.setState({ showPortal: true });

            // Don't let the event set focus or start a mouse selection.
            event.preventDefault();
        }
    };

    private updateSize(event: MouseEvent | KeyboardEvent, sizeChange: number, applyKeyboardMultiplier: boolean = false): number {
        // Compute the udpated size of the Sized element.
        const currentSize = ObservableLike.getValue(this.props.size);
        const multiplier = this.props.position === Position.far ? -1 : 1;
        const keyboardStepMultiplier = applyKeyboardMultiplier ? this.props.keyboardStepMultiplier || 1 : 1;
        const updatedSize = Math.floor(Math.min(this.props.maxSize!, Math.max(this.props.minSize!, currentSize + sizeChange * multiplier * keyboardStepMultiplier)));

        // Notify the caller of the updated size.
        this.props.onSize(event, updatedSize, this.props.id);

        // console.log("currentSize = " + currentSize + " updateSize = " + updatedSize);
        return (updatedSize - currentSize) * multiplier;
    }
}

/**
 * The Sized function is used to produce a div that has a fixed width or height
 * based on the orientation of the sized props. This is a basic component that
 * can be used with the Sizer to produce a basic splitter like UI.
 *
 * @param props properties to render the appropriate container element given the
 * props.
 */
export function Sized(props: ISizedProps & { children?: React.ReactNode }): JSX.Element {
    return (
        <Observer height={props.height} width={props.width}>
            {(observedProps: { height?: number; width?: number }) => {
                const style: { height?: string; width?: string } = {};

                // Add any specific height that has been defined.
                if (observedProps.height !== undefined) {
                    style.height = observedProps.height + "px";
                }

                // Add any specific width that has been defined.
                if (observedProps.width !== undefined) {
                    style.width = observedProps.width + "px";
                }

                return (
                    <div className={css(props.className, "flex-noshrink")} style={style}>
                        {props.children}
                    </div>
                );
            }}
        </Observer>
    );
}
